import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Type, Minus, Plus, MoreVertical, Download, CheckCircle2, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  storeBulkTranslation,
  listOfflineManifests,
  removeOfflineTranslation,
} from '../utils/offlineBibleStore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import { mockTranslations } from '../mockData';
import { getBookName } from '../utils/bookTranslations';
import { isBookAvailable, isOfflineAvailable, otBooksFor, NT_BOOKS } from '../constants/bibleConstants';
import { metaFor } from '../constants/translationMeta';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TranslationInfoButton = ({ abbr, language }) => {
  const es = language === 'es';
  const meta = metaFor(abbr);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" data-testid="translation-info-btn" aria-label={es ? 'Acerca de esta versión' : 'About this version'}>
          <Info className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 z-[100] bg-popover shadow-lg" data-testid="translation-info-popover">
        {meta ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{abbr}</span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${meta.style === 'modern' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'}`}>
                {meta.style === 'modern' ? (es ? 'Moderna' : 'Modern') : (es ? 'Clásica' : 'Classic')}
              </span>
              <span className="text-xs text-muted-foreground">· {meta.era}</span>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">{es ? meta.es : meta.en}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{es ? 'Sin información disponible.' : 'No info available.'}</p>
        )}
      </PopoverContent>
    </Popover>
  );
};


/**
 * Bible Reader Header Component
 * Mobile-optimized header with translation + settings on row 1, book + chapter on row 2
 */
const BibleReaderHeader = ({
  selectedTranslation,
  setSelectedTranslation,
  selectedBook,
  setSelectedBook,
  selectedChapter,
  setSelectedChapter,
  totalChapters,
  fontSize,
  setFontSize,
  fontFamily,
  setFontFamily,
  language
}) => {
  // Per-version offline download state (YouVersion-style): each version row in
  // the dropdown gets its own download / downloaded / remove control.
  const [downloadedSet, setDownloadedSet] = useState(new Set());
  const [dlState, setDlState] = useState({}); // { [abbr]: progress }

  const refreshManifests = useCallback(async () => {
    try {
      const list = await listOfflineManifests();
      setDownloadedSet(new Set((list || []).map((m) => m.translation)));
    } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { refreshManifests(); }, [refreshManifests]);

  const handleVersionDownload = useCallback(async (abbr) => {
    if (dlState[abbr]) return;
    setDlState((s) => ({ ...s, [abbr]: 5 }));
    try {
      const verses = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/bible/bulk?translation=${abbr}`);
        xhr.responseType = 'json';
        xhr.onprogress = (e) => {
          const pct = e.lengthComputable ? Math.round((e.loaded / e.total) * 70) : null;
          setDlState((s) => (s[abbr] != null ? { ...s, [abbr]: pct ?? Math.min(65, (s[abbr] || 0) + 2) } : s));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve((xhr.response && xhr.response.verses) || []);
          else reject(new Error(`HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      if (!verses.length) throw new Error('empty');
      setDlState((s) => ({ ...s, [abbr]: 85 }));
      await storeBulkTranslation(abbr, verses);
      toast.success(language === 'es'
        ? `${abbr}: ${verses.length.toLocaleString()} versículos sin conexión`
        : `${abbr}: ${verses.length.toLocaleString()} verses saved offline`);
      await refreshManifests();
    } catch (e) {
      toast.error(language === 'es' ? `Error al descargar ${abbr}` : `Failed to download ${abbr}`);
    } finally {
      setDlState((s) => { const { [abbr]: _x, ...rest } = s; return rest; });
    }
  }, [dlState, language, refreshManifests]);

  const handleVersionRemove = useCallback(async (abbr) => {
    try {
      await removeOfflineTranslation(abbr);
      toast.info(language === 'es' ? `${abbr} eliminada del dispositivo` : `${abbr} removed from device`);
      await refreshManifests();
    } catch (_) { /* noop */ }
  }, [language, refreshManifests]);

  // A download / downloaded / downloading control. Rendered as an overlay on
  // the row (a sibling of SelectItem, not inside it) so clicking it never
  // triggers Radix's item-selection and never clones into the trigger value.
  const VersionDownloadControl = ({ abbr }) => {
    if (!isOfflineAvailable(abbr)) return null;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    const downloaded = downloadedSet.has(abbr);
    const progress = dlState[abbr];
    const isDownloading = progress != null;
    if (isDownloading) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] text-amber-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress}%
        </span>
      );
    }
    if (downloaded) {
      return (
        <button
          type="button"
          onPointerDownCapture={stop}
          onPointerUpCapture={stop}
          onClick={(e) => { stop(e); handleVersionRemove(abbr); }}
          className="inline-flex items-center gap-1 text-[10px] text-emerald-500 hover:text-red-500 group/dl"
          title={language === 'es' ? 'Quitar descarga' : 'Remove download'}
          data-testid={`version-remove-${abbr}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5 group-hover/dl:hidden" />
          <Trash2 className="w-3.5 h-3.5 hidden group-hover/dl:inline" />
        </button>
      );
    }
    return (
      <button
        type="button"
        onPointerDownCapture={stop}
        onPointerUpCapture={stop}
        onClick={(e) => { stop(e); handleVersionDownload(abbr); }}
        className="inline-flex items-center text-muted-foreground hover:text-amber-500"
        title={language === 'es' ? 'Descargar sin conexión' : 'Download for offline'}
        data-testid={`version-download-${abbr}`}
      >
        <Download className="w-3.5 h-3.5" />
      </button>
    );
  };

  const renderVersionItem = (trans) => (
    <div key={trans.id} className="relative">
      <SelectItem value={trans.abbr} className="pr-9">
        <span className="truncate">{trans.abbr} - {trans.name}</span>
      </SelectItem>
      {isOfflineAvailable(trans.abbr) && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
          <VersionDownloadControl abbr={trans.abbr} />
        </div>
      )}
    </div>
  );

  return (
    <div className="mb-6 space-y-3">
      {/* Row 1: Translation Selector + Font Settings Dropdown */}
      <div className="flex items-center gap-2">
        {/* Translation Selector - Takes most space */}
        <div className="flex-1">
          <Select value={selectedTranslation} onValueChange={setSelectedTranslation}>
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectGroup>
                <SelectLabel>English</SelectLabel>
                <div className="px-2 pb-1.5 text-[11px] text-muted-foreground">
                  {language === 'es' ? 'Gratis · sin conexión · con Strong\u2019s' : 'Free · offline-ready · Strong\u2019s included'}
                </div>
                {mockTranslations.filter(t => t.language === 'English').map(renderVersionItem)}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>Español</SelectLabel>
                <div className="px-2 pb-1.5 text-[11px] text-muted-foreground">
                  {language === 'es' ? 'Gratis · sin conexión · con Strong\u2019s' : 'Free · offline-ready · Strong\u2019s included'}
                </div>
                {mockTranslations.filter(t => t.language === 'Español').map(renderVersionItem)}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Version info popover */}
        <TranslationInfoButton abbr={selectedTranslation} language={language} />

        {/* 3-Dot Menu for Font Controls */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-10 w-10 shrink-0">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">{language === 'es' ? 'Ajustes de texto' : 'Text settings'}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex items-center gap-2">
              <Type className="w-4 h-4" />
              {language === 'es' ? 'Ajustes de Texto' : 'Text Settings'}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Font Size Control */}
            <div className="px-2 py-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                {language === 'es' ? 'Tamaño de Letra' : 'Font Size'}
              </div>
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    const sizes = ['sm', 'base', 'lg', 'xl', '2xl'];
                    const currentIndex = sizes.indexOf(fontSize);
                    if (currentIndex > 0) setFontSize(sizes[currentIndex - 1]);
                  }}
                  disabled={fontSize === 'sm'}
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-sm font-medium min-w-[80px] text-center">
                  {fontSize === 'sm' && (language === 'es' ? 'Pequeño' : 'Small')}
                  {fontSize === 'base' && (language === 'es' ? 'Normal' : 'Normal')}
                  {fontSize === 'lg' && (language === 'es' ? 'Grande' : 'Large')}
                  {fontSize === 'xl' && (language === 'es' ? 'Muy Grande' : 'X-Large')}
                  {fontSize === '2xl' && (language === 'es' ? 'Enorme' : 'XX-Large')}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    const sizes = ['sm', 'base', 'lg', 'xl', '2xl'];
                    const currentIndex = sizes.indexOf(fontSize);
                    if (currentIndex < sizes.length - 1) setFontSize(sizes[currentIndex + 1]);
                  }}
                  disabled={fontSize === '2xl'}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>

            <DropdownMenuSeparator />

            {/* Font Family Control */}
            <div className="px-2 py-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                {language === 'es' ? 'Fuente' : 'Font Family'}
              </div>
              <div className="flex gap-2">
                <Button
                  variant={fontFamily === 'serif' ? 'default' : 'outline'}
                  size="sm"
                  className="font-serif h-8 flex-1"
                  onClick={() => setFontFamily('serif')}
                >
                  Aa
                </Button>
                <Button
                  variant={fontFamily === 'sans' ? 'default' : 'outline'}
                  size="sm"
                  className="font-sans h-8 flex-1"
                  onClick={() => setFontFamily('sans')}
                >
                  Aa
                </Button>
                <Button
                  variant={fontFamily === 'mono' ? 'default' : 'outline'}
                  size="sm"
                  className="font-mono h-8 flex-1"
                  onClick={() => setFontFamily('mono')}
                >
                  Aa
                </Button>
              </div>
            </div>

            {/* Offline downloads moved to a per-version control in the version
                dropdown (YouVersion-style), so no global download item here. */}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row 2: Book + Chapter Selectors (Side by Side) */}
      <div className="flex gap-2">
        {/* Book Selector */}
        <Select value={selectedBook} onValueChange={setSelectedBook}>
          <SelectTrigger className="h-10 flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectGroup>
              <SelectLabel>Old Testament</SelectLabel>
              {otBooksFor(selectedTranslation).map(book => (
                <SelectItem key={book} value={book} disabled={!isBookAvailable(selectedTranslation, book)}>
                  {getBookName(book, selectedTranslation, language)}
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>New Testament</SelectLabel>
              {NT_BOOKS.map(book => (
                <SelectItem key={book} value={book} disabled={!isBookAvailable(selectedTranslation, book)}>
                  {getBookName(book, selectedTranslation, language)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {/* Chapter Selector */}
        <Select value={selectedChapter.toString()} onValueChange={(val) => setSelectedChapter(parseInt(val))}>
          <SelectTrigger className="h-10 w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {Array.from({ length: totalChapters }, (_, i) => i + 1).map(ch => (
              <SelectItem key={ch} value={ch.toString()}>
                {language === 'es' ? 'Cap' : 'Ch'} {ch}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default BibleReaderHeader;
